import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useFamilyRoles } from 'src/clients/hooks/useFamilyRoles';
import type { AddParticipantBody } from 'src/meetings/hooks/useMeetingParticipants';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';
import { useProfessionalConnections } from './useProfessionalConnections';

export type AttendeeGroup = 'adviser' | 'guest' | 'keyContact';

export type Attendee = {
  id: string;
  name: string;
  role: string;
  group: AttendeeGroup;
  participantId: string | null;
  defaultChecked: boolean;
  addBody: AddParticipantBody;
};

/**
 * Builds the attendee list shown in the meeting member-selection modals:
 * the advisor, every family member (for a family scope), plus anyone already on
 * the meeting who isn't one of those. For an individual scope the client is
 * always a participant on the event, so they surface via the existing-participant
 * pass. `defaultChecked` reflects who is currently on the meeting.
 */
export function useMeetingAttendees(scope: MeetingScope, meeting: FamilyMeeting): Attendee[] {
  const familyId = scope.kind === 'family' ? scope.familyId : undefined;
  const { family } = useFamily(familyId);
  const { clients } = useClients({ familyId }, { enabled: !!familyId });
  const { familyRoles } = useFamilyRoles();
  const { currentUser } = useCurrentUser();
  const connections = useProfessionalConnections(scope);

  const connectionNames = new Set(
    connections.map(connection => connection.name.trim().toLowerCase()),
  );

  return buildAttendees({
    scope,
    family,
    clients,
    familyRoles,
    currentUser,
    meeting,
    connectionNames,
  });
}

type Family = NonNullable<ReturnType<typeof useFamily>['family']>;
type Client = ReturnType<typeof useClients>['clients'][number];
type FamilyRole = ReturnType<typeof useFamilyRoles>['familyRoles'][number];
type CurrentUser = ReturnType<typeof useCurrentUser>['currentUser'];
type MeetingParticipant = FamilyMeeting['eventParticipants'][number]['participant'];

function buildAttendees({
  scope,
  family,
  clients,
  familyRoles,
  currentUser,
  meeting,
  connectionNames,
}: {
  scope: MeetingScope;
  family: Family | null;
  clients: Client[];
  familyRoles: FamilyRole[];
  currentUser: CurrentUser;
  meeting: FamilyMeeting;
  connectionNames: Set<string>;
}): Attendee[] {
  const clientById = Object.fromEntries(clients.map(client => [client.id, client]));
  const roleNameById = Object.fromEntries(familyRoles.map(role => [role.id, role.name]));

  const participantByKey = new Map<
    string,
    { participantId: string | null; participant: MeetingParticipant }
  >();
  for (const eventParticipant of meeting.eventParticipants) {
    const participant = eventParticipant.participant;
    participantByKey.set(participantKey(participant), {
      participantId: participant.id,
      participant,
    });
  }

  const attendees: Attendee[] = [];
  const representedKeys = new Set<string>();

  if (currentUser) {
    const key = `user:${currentUser.id}`;
    representedKeys.add(key);
    attendees.push({
      id: key,
      name: `${currentUser.firstName ?? ''} (You)`.trim(),
      role: 'Adviser',
      group: 'adviser',
      participantId: participantByKey.get(key)?.participantId ?? null,
      defaultChecked: participantByKey.has(key),
      addBody: { participantType: 'USER', userId: currentUser.id },
    });
  }

  for (const clientId of scopedClientIds(scope, family)) {
    const client = clientById[clientId];
    if (!client) continue;
    const key = `client:${clientId}`;
    representedKeys.add(key);
    attendees.push({
      id: key,
      name: `${client.firstName} ${client.lastName}`.trim(),
      role: familyMemberRole(family, roleNameById, clientId) ?? 'Family member',
      group: 'guest',
      participantId: participantByKey.get(key)?.participantId ?? null,
      defaultChecked: participantByKey.has(key),
      addBody: { participantType: 'CLIENT', clientId },
    });
  }

  // Anyone already on the meeting who isn't the advisor or a scoped client —
  // e.g. a client added via "Choose a client", or an external attendee.
  for (const [key, { participantId, participant }] of participantByKey) {
    if (representedKeys.has(key)) continue;
    attendees.push({
      id: key,
      participantId,
      defaultChecked: true,
      ...extraAttendeeFields(participant, family, roleNameById, connectionNames),
    });
  }

  return attendees;
}

function scopedClientIds(scope: MeetingScope, family: Family | null): string[] {
  if (scope.kind === 'individual') return [];

  const seen = new Set<string>();
  return [
    family?.mainFamilyMember.clientId,
    ...(family?.secondaryFamilyMembers ?? []).map(s => s.clientId),
    ...(family?.members ?? []).map(m => m.clientId),
  ].filter((id): id is string => !!id && !seen.has(id) && (seen.add(id), true));
}

function familyMemberRole(
  family: Family | null,
  roleNameById: Record<string, string>,
  clientId: string,
): string | null {
  if (!family) return null;

  // Role model: the role lives on the members entry. In the star model that same
  // entry is a role-less peer edge, so only treat it as authoritative when it
  // actually carries a role id.
  const member = family.members.find(m => m.clientId === clientId);
  if (member?.familyRoleId && roleNameById[member.familyRoleId]) {
    return roleNameById[member.familyRoleId];
  }

  if (family.mainFamilyMember.clientId === clientId) {
    const roleId = family.mainFamilyMember.familyRoleId;
    if (roleId && roleNameById[roleId]) return roleNameById[roleId];
  }

  // Star model: the label is the relationship to the main family member.
  const secondary = family.secondaryFamilyMembers.find(s => s.clientId === clientId);
  if (secondary?.relationshipToMainFamilyMember) {
    return secondary.relationshipToMainFamilyMember;
  }

  const inFamily =
    family.mainFamilyMember.clientId === clientId || Boolean(member) || Boolean(secondary);
  return inFamily ? 'Family member' : null;
}

function participantKey(participant: MeetingParticipant): string {
  if (participant.client?.id) return `client:${participant.client.id}`;
  if (participant.user?.id) return `user:${participant.user.id}`;
  return `participant:${participant.id ?? ''}`;
}

function extraAttendeeFields(
  participant: MeetingParticipant,
  family: Family | null,
  roleNameById: Record<string, string>,
  connectionNames: Set<string>,
): {
  name: string;
  role: string;
  group: AttendeeGroup;
  addBody: AddParticipantBody;
} {
  if (participant.client?.id) {
    return {
      name: `${participant.client.firstName} ${participant.client.lastName}`.trim(),
      role: familyMemberRole(family, roleNameById, participant.client.id) ?? 'Client',
      group: 'guest',
      addBody: { participantType: 'CLIENT', clientId: participant.client.id },
    };
  }
  if (participant.user?.id) {
    return {
      name: `${participant.user.firstName ?? ''} ${participant.user.lastName ?? ''}`.trim(),
      role: 'Adviser',
      group: 'adviser',
      addBody: { participantType: 'USER', userId: participant.user.id },
    };
  }
  const name = participant.name ?? 'Guest';
  const isKeyContact = connectionNames.has(name.trim().toLowerCase());
  return {
    name,
    role: participant.description ?? 'Guest',
    group: isKeyContact ? 'keyContact' : 'guest',
    addBody: {
      participantType: 'EXTERNAL',
      name: participant.name ?? '',
      description: participant.description ?? '',
    },
  };
}
